import React, { useState, PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { i18n_sign_in } from '@yi/i18n/phrases/i18n_sign_in';

import { useCurrentUser } from '~/src/common/user';
import { useHasMounted } from '~/src/components/hooks';
import { useLocale } from '~/src/components/LocaleProvider';
import { SignInModal } from '~/src/components/modals';
import { sizeTabletMin, breakpoints } from '~/styles';

import {
  IconLogoResponsive,
  IconLogoTextResponsive as IconLogoTextResponsiveBase,
  ItemWrapper,
  LogoAnchor,
} from './elements';
import { Button } from '../buttons';

const IconLogoTextResponsive = styled(IconLogoTextResponsiveBase)`
  display: none;
  ${sizeTabletMin(css`
    display: block;
  `)}
`;

const styles = {
  container: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    [breakpoints.minMobileLandscape]: {
      justifyContent: 'end',
    },
  }),
  logo: css({
    [breakpoints.minMobileLandscape]: {
      flexBasis: '33.3333%',
      flexGrow: '0',
      maxWidth: '33.3333%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
  btnContainer: css({
    [breakpoints.minMobileLandscape]: {
      flexBasis: '33.3333%',
      flexGrow: '0',
      maxWidth: '33.3333%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
  }),
};

export const SimpleNavWithSignIn: React.FC<PropsWithChildren> = () => {
  const { locale } = useLocale();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { loading, isLoggedIn } = useCurrentUser();
  const { hasMounted } = useHasMounted();
  const isFirstRender = !hasMounted;

  if (loading || isFirstRender || isLoggedIn) {
    return (
      <Grid container alignItems="center" justifyContent="center">
        <LogoAnchor to="/" marginRight>
          <IconLogoResponsive />
        </LogoAnchor>
        <LogoAnchor to="/">
          <IconLogoTextResponsive style={{ marginBottom: 5 }} />
        </LogoAnchor>
      </Grid>
    );
  }
  return (
    <>
      <div css={styles.container}>
        <div css={styles.logo}>
          <LogoAnchor to="/" marginRight>
            <IconLogoResponsive />
          </LogoAnchor>
          <LogoAnchor to="/">
            <IconLogoTextResponsive style={{ marginBottom: 5 }} />
          </LogoAnchor>
        </div>
        <ItemWrapper css={styles.btnContainer}>
          <Button
            onClick={() => setShowSignInModal(true)}
            data-testid="signin-open-modal"
            aria-label="Sign In"
            kind="text"
          >
            {i18n_sign_in(locale)}
          </Button>
        </ItemWrapper>
      </div>
      <SignInModal open={showSignInModal} setOpen={setShowSignInModal} />
    </>
  );
};
